/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  IoBusinessOutline,
  IoSearchOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoWarning,
  IoSwapVerticalOutline,
} from 'react-icons/io5'

export interface DistributorStockItem {
  dist_id: number
  nome: string
  produtoMaior: string
  estoque: number
  sellout3M?: number
  dias: number
  mesesHistorico?: number
}

interface DistributorStockAnalysisProps {
  data: DistributorStockItem[]
  diasEstoqueGeral: number
  totalSellout3MGeral?: number
}

type SortField = 'estoque' | 'sellout' | 'dias' | 'nome'
type StatusFilter = 'TODOS' | 'Equilibrado' | 'Excesso' | 'Ruptura'

export default function DistributorStockAnalysis({
  data,
  diasEstoqueGeral,
  totalSellout3MGeral,
}: DistributorStockAnalysisProps) {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('estoque')
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('TODOS')

  // Totais consolidados
  const totals = useMemo(() => {
    const totalEstoque = data.reduce((acc, curr) => acc + curr.estoque, 0)
    const totalSellout =
      totalSellout3MGeral ?? data.reduce((acc, curr) => acc + (curr.sellout3M ?? 0), 0)
    const totalDistribuidores = data.length

    return {
      totalEstoque,
      totalSellout,
      totalDistribuidores,
    }
  }, [data, totalSellout3MGeral])

  const getStatusFromDays = (dias: number) => {
    if (dias > 90) return 'Excesso'
    if (dias < 15 && dias > 0) return 'Ruptura'
    if (dias === 0) return 'Sem Giro'
    return 'Equilibrado'
  }

  const getStatusBadgeConfig = (dias: number) => {
    const st = getStatusFromDays(dias)
    switch (st) {
      case 'Equilibrado':
        return {
          color: '#a6c10b',
          bg: 'rgba(166, 193, 11, 0.15)',
          border: 'rgba(166, 193, 11, 0.35)',
          icon: <IoCheckmarkCircle size={13} />,
          text: 'Equilibrado (15-90d)',
        }
      case 'Excesso':
        return {
          color: '#ff4d4f',
          bg: 'rgba(255, 77, 79, 0.15)',
          border: 'rgba(255, 77, 79, 0.35)',
          icon: <IoAlertCircle size={13} />,
          text: 'Excesso (> 90d)',
        }
      case 'Ruptura':
        return {
          color: '#ffa940',
          bg: 'rgba(255, 169, 64, 0.15)',
          border: 'rgba(255, 169, 64, 0.35)',
          icon: <IoWarning size={13} />,
          text: 'Ruptura (< 15d)',
        }
      default:
        return {
          color: '#8e9aaf',
          bg: 'rgba(142, 154, 175, 0.15)',
          border: 'rgba(142, 154, 175, 0.35)',
          icon: <IoAlertCircle size={13} />,
          text: 'Sem Giro',
        }
    }
  }

  // Filtragem e Ordenação
  const processedData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return data
      .filter((item) => {
        const matchesSearch =
          !term ||
          item.nome.toLowerCase().includes(term) ||
          item.produtoMaior.toLowerCase().includes(term) ||
          String(item.dist_id).includes(term)

        const st = getStatusFromDays(item.dias)
        const matchesStatus = statusFilter === 'TODOS' || st === statusFilter

        return matchesSearch && matchesStatus
      })
      .sort((a, b) => {
        let valA: any = a[sortField === 'sellout' ? 'sellout3M' : sortField] || 0
        let valB: any = b[sortField === 'sellout' ? 'sellout3M' : sortField] || 0

        if (sortField === 'nome') {
          return sortOrder === 'asc' ? a.nome.localeCompare(b.nome) : b.nome.localeCompare(a.nome)
        }

        return sortOrder === 'desc' ? valB - valA : valA - valB
      })
  }, [data, searchTerm, sortField, sortOrder, statusFilter])

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  return (
    <Container>
      {/* CABEÇALHO DO MÓDULO */}
      <HeaderWrapper>
        <div className='title-area'>
          <h3>
            <span className='dot' /> Estoque por Distribuidor (Análise 1 para 1)
          </h3>
          <p className='subtitle'>
            Monitoramento individual de inventário, giro trimestral, cobertura e produto líder
          </p>
        </div>

        {/* CONTROLES (BUSCA E ORDENAÇÃO) */}
        <ControlsArea>
          <SearchWrapper>
            <IoSearchOutline className='search-icon' />
            <SearchInput
              type='text'
              placeholder='Buscar distribuidor ou produto...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className='clear-btn' onClick={() => setSearchTerm('')}>
                ×
              </button>
            )}
          </SearchWrapper>

          <SortSelectWrapper>
            <IoSwapVerticalOutline className='sort-icon' />
            <SortSelect
              value={`${sortField}_${sortOrder}`}
              onChange={(e) => {
                const [f, o] = e.target.value.split('_') as [SortField, 'desc' | 'asc']
                setSortField(f)
                setSortOrder(o)
              }}
            >
              <option value='estoque_desc'>Maior Estoque</option>
              <option value='estoque_asc'>Menor Estoque</option>
              <option value='sellout_desc'>Maior Sellout 3M</option>
              <option value='sellout_asc'>Menor Sellout 3M</option>
              <option value='dias_desc'>Mais Dias de Estoque</option>
              <option value='dias_asc'>Menos Dias de Estoque</option>
              <option value='nome_asc'>Nome (A-Z)</option>
            </SortSelect>
          </SortSelectWrapper>
        </ControlsArea>
      </HeaderWrapper>

      {/* RIBBON DE RESUMO E STATUS */}
      <SummaryRibbon>
        <div className='chips-group'>
          <span className='filter-label'>Status:</span>
          {(['TODOS', 'Equilibrado', 'Excesso', 'Ruptura'] as StatusFilter[]).map((st) => (
            <StatusFilterChip
              key={st}
              active={statusFilter === st}
              status={st}
              onClick={() => setStatusFilter(st)}
            >
              {st === 'TODOS' ? 'Todos' : st}
            </StatusFilterChip>
          ))}
        </div>

        <div className='kpis-group'>
          <KpiPill>
            <span className='label'>Distribuidores:</span>
            <span className='val'>{processedData.length} / {totals.totalDistribuidores}</span>
          </KpiPill>
          <KpiPill>
            <span className='label'>Estoque Total:</span>
            <span className='val highlight'>{Math.round(totals.totalEstoque).toLocaleString('pt-BR')} Cx</span>
          </KpiPill>
          <KpiPill>
            <span className='label'>Sellout 3M Total:</span>
            <span className='val'>{Math.round(totals.totalSellout).toLocaleString('pt-BR')} Cx</span>
          </KpiPill>
          <KpiPill accent>
            <span className='label'>Dias Geral:</span>
            <span className='val'>{diasEstoqueGeral > 0 ? Math.round(diasEstoqueGeral) : 0} dias</span>
          </KpiPill>
        </div>
      </SummaryRibbon>

      {/* RENDERIZAÇÃO DIRETA EM TABELA ENRIQUECIDA */}
      {processedData.length === 0 ? (
        <EmptyBox>
          <IoBusinessOutline size={38} className='empty-icon' />
          <p>Nenhum distribuidor encontrado com os filtros selecionados.</p>
          {(searchTerm || statusFilter !== 'TODOS') && (
            <button
              className='reset-btn'
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('TODOS')
              }}
            >
              Limpar Filtros
            </button>
          )}
        </EmptyBox>
      ) : (
        <TableContainer>
          <EnhancedTable>
            <thead>
              <tr>
                <th style={{ width: '60px', textAlign: 'center' }}>Rank</th>
                <th onClick={() => handleSortChange('nome')} style={{ cursor: 'pointer' }}>
                  Distribuidor {sortField === 'nome' && (sortOrder === 'desc' ? '▼' : '▲')}
                </th>
                <th style={{ width: '130px' }}>Participação</th>
                <th
                  onClick={() => handleSortChange('estoque')}
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                >
                  Estoque (Cx) {sortField === 'estoque' && (sortOrder === 'desc' ? '▼' : '▲')}
                </th>
                <th
                  onClick={() => handleSortChange('sellout')}
                  style={{ textAlign: 'right', cursor: 'pointer' }}
                >
                  Sellout 3M (Cx) {sortField === 'sellout' && (sortOrder === 'desc' ? '▼' : '▲')}
                </th>
                <th style={{ textAlign: 'right' }}>Giro Médio (Cx/mês)</th>
                <th
                  onClick={() => handleSortChange('dias')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  Dias {sortField === 'dias' && (sortOrder === 'desc' ? '▼' : '▲')}
                </th>
                <th>Produto Maior Volume</th>
              </tr>
            </thead>
            <tbody>
              {processedData.map((dist, idx) => {
                const share =
                  totals.totalEstoque > 0 ? (dist.estoque / totals.totalEstoque) * 100 : 0
                const statusCfg = getStatusBadgeConfig(dist.dias)
                const meses =
                  dist.mesesHistorico && dist.mesesHistorico > 0
                    ? Math.min(dist.mesesHistorico, 3)
                    : 3
                const mediaMensal = dist.sellout3M ? Math.round(dist.sellout3M / meses) : 0
                const hasPartialHistory = Boolean(
                  dist.mesesHistorico && dist.mesesHistorico > 0 && dist.mesesHistorico < 3
                )

                return (
                  <tr key={dist.dist_id || idx}>
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: isLight ? '#4d7c0f' : '#a6c10b' }}>
                      #{idx + 1}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontWeight: 600, color: isLight ? '#0f172a' : '#fff' }}>{dist.nome}</span>
                          {hasPartialHistory && (
                            <HistoricoBadge
                              title={`Média calculada sobre ${dist.mesesHistorico} ${
                                dist.mesesHistorico === 1 ? 'mês' : 'meses'
                              } de histórico de vendas`}
                            >
                              {dist.mesesHistorico}M
                            </HistoricoBadge>
                          )}
                        </div>
                        <span style={{ fontSize: '10px', color: isLight ? '#64748b' : '#94a3b8' }}>ID: {dist.dist_id}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ShareBarTrack style={{ height: '5px', flex: 1 }}>
                          <ShareBarFill pct={Math.min(share, 100)} />
                        </ShareBarTrack>
                        <span style={{ fontSize: '10px', color: isLight ? '#334155' : '#94a3b8', minWidth: '32px', fontWeight: isLight ? 600 : 400 }}>
                          {share.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: isLight ? '#0f172a' : '#fff' }}>
                      {Math.round(dist.estoque).toLocaleString('pt-BR')}
                    </td>
                    <td style={{ textAlign: 'right', color: isLight ? '#0f172a' : '#cbd5e1' }}>
                      {Math.round(dist.sellout3M ?? 0).toLocaleString('pt-BR')}
                    </td>
                    <td style={{ textAlign: 'right', color: isLight ? '#0f172a' : '#cbd5e1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        <span>{mediaMensal.toLocaleString('pt-BR')}</span>
                        {hasPartialHistory && (
                          <span
                            style={{
                              fontSize: '9px',
                              padding: '1px 3px',
                              borderRadius: '3px',
                              backgroundColor: 'rgba(245, 158, 11, 0.15)',
                              color: '#f59e0b',
                            }}
                            title={`Média dividida por ${dist.mesesHistorico} ${
                              dist.mesesHistorico === 1 ? 'mês' : 'meses'
                            }`}
                          >
                            /{dist.mesesHistorico}m
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <StatusPill mini color={statusCfg.color} bg={statusCfg.bg} border={statusCfg.border}>
                          {statusCfg.icon}
                          <span>{dist.dias}d</span>
                        </StatusPill>
                      </div>
                    </td>
                    <td style={{ maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isLight ? '#0f172a' : '#cbd5e1' }} title={dist.produtoMaior}>
                      {dist.produtoMaior || '-'}
                    </td>
                  </tr>
                )
              })}
              {/* LINHA DE TOTAL */}
              <tr className='table-total-row'>
                <td style={{ textAlign: 'center' }}>-</td>
                <td><strong>Total ({processedData.length} distribuidores)</strong></td>
                <td>100.0%</td>
                <td style={{ textAlign: 'right', color: isLight ? '#15803d' : '#a6c10b', fontWeight: 'bold' }}>
                  {Math.round(processedData.reduce((acc, curr) => acc + curr.estoque, 0)).toLocaleString('pt-BR')}
                </td>
                <td style={{ textAlign: 'right', color: isLight ? '#0f172a' : '#cbd5e1' }}>
                  {Math.round(processedData.reduce((acc, curr) => acc + (curr.sellout3M ?? 0), 0)).toLocaleString('pt-BR')}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {Math.round(
                    processedData.reduce((acc, curr) => {
                      const m =
                        curr.mesesHistorico && curr.mesesHistorico > 0
                          ? Math.min(curr.mesesHistorico, 3)
                          : 3
                      return acc + (curr.sellout3M ? curr.sellout3M / m : 0)
                    }, 0)
                  ).toLocaleString('pt-BR')}
                </td>
                <td style={{ textAlign: 'center', color: '#a6c10b' }}>
                  {diasEstoqueGeral > 0 ? Math.round(diasEstoqueGeral) : 0}d
                </td>
                <td>-</td>
              </tr>
            </tbody>
          </EnhancedTable>
        </TableContainer>
      )}
    </Container>
  )
}

// --- ESTILOS DO MÓDULO DE DISTRIBUIDORES ---

const Container = styled.div`
  margin-top: 15px;
  margin-bottom: 25px;
  font-family: 'Montserrat', sans-serif;
`

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 12px;

  .title-area {
    h3 {
      color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
      font-size: 16px;
      font-weight: 700;
      margin: 0 0 4px 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .dot {
      width: 10px;
      height: 10px;
      background: #a6c10b;
      border-radius: 50%;
      box-shadow: 0 0 12px #a6c10b;
    }
    .subtitle {
      color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
      font-size: 12px;
      margin: 0;
    }
  }
`

const ControlsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 10px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: 14px;
  }

  .clear-btn {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: 16px;
    cursor: pointer;
    padding: 2px 5px;
  }
`

const SearchInput = styled.input`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.5)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 8px;
  padding: 7px 26px 7px 30px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  outline: none;
  width: 220px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #a6c10b;
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.8)')};
    box-shadow: 0 0 10px rgba(166, 193, 11, 0.2);
    width: 240px;
  }

  &::placeholder {
    color: ${(props) => (props.theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.35)')};
  }
`

const SortSelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .sort-icon {
    position: absolute;
    left: 8px;
    color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};
    font-size: 14px;
    pointer-events: none;
  }
`

const SortSelect = styled.select`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.5)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 8px;
  padding: 7px 12px 7px 28px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  outline: none;
  cursor: pointer;

  option {
    background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : '#001d53')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
  }
`

const SummaryRibbon = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 0, 0, 0.25)')};
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.06)')};
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 2px 8px rgba(0,0,0,0.04)' : 'none')};
  margin-bottom: 14px;

  .chips-group {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;

    .filter-label {
      color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#8e9aaf')};
      font-size: 11px;
      font-weight: 600;
      margin-right: 2px;
    }
  }

  .kpis-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
`

const StatusFilterChip = styled.button<{ active: boolean; status: string }>`
  background: ${(props) => {
    if (!props.active) return props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)'
    if (props.status === 'Equilibrado') return 'rgba(166, 193, 11, 0.2)'
    if (props.status === 'Excesso') return 'rgba(255, 77, 79, 0.2)'
    if (props.status === 'Ruptura') return 'rgba(255, 169, 64, 0.2)'
    return props.theme.name === 'light' ? 'rgba(0, 31, 92, 0.15)' : 'rgba(255, 255, 255, 0.2)'
  }};
  color: ${(props) => {
    if (!props.active) return props.theme.name === 'light' ? '#475569' : '#94a3b8'
    if (props.status === 'Equilibrado') return props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b'
    if (props.status === 'Excesso') return '#ff4d4f'
    if (props.status === 'Ruptura') return '#ffa940'
    return props.theme.name === 'light' ? '#0f172a' : '#fff'
  }};
  border: 1px solid
    ${(props) => {
      if (!props.active) return props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'
      if (props.status === 'Equilibrado') return '#a6c10b'
      if (props.status === 'Excesso') return '#ff4d4f'
      if (props.status === 'Ruptura') return '#ffa940'
      return props.theme.name === 'light' ? '#001f5c' : '#fff'
    }};
  padding: 3px 8px;
  border-radius: 14px;
  font-size: 10px;
  font-weight: ${(props) => (props.active ? '700' : '500')};
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
`

const KpiPill = styled.div<{ accent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'light' ? '#475569' : '#94a3b8')};
  background: ${(props) =>
    props.accent
      ? props.theme.name === 'light'
        ? 'rgba(166, 193, 11, 0.18)'
        : 'rgba(166, 193, 11, 0.12)'
      : props.theme.name === 'light'
      ? '#f1f5f9'
      : 'rgba(255, 255, 255, 0.03)'};
  padding: 3px 7px;
  border-radius: 5px;
  border: 1px solid
    ${(props) =>
      props.accent
        ? props.theme.name === 'light'
          ? '#a6c10b'
          : 'rgba(166, 193, 11, 0.3)'
        : props.theme.name === 'light'
        ? '#cbd5e1'
        : 'rgba(255, 255, 255, 0.05)'};

  .label {
    font-weight: 500;
  }
  .val {
    color: ${(props) =>
      props.accent
        ? props.theme.name === 'light'
          ? '#4d7c0f'
          : '#a6c10b'
        : props.theme.name === 'light'
        ? '#0f172a'
        : '#fff'};
    font-weight: 700;
  }
  .val.highlight {
    color: ${(props) => (props.theme.name === 'light' ? '#4d7c0f' : '#a6c10b')};
  }
`

const StatusPill = styled.div<{ color: string; bg: string; border: string; mini?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${(props) => props.bg};
  color: ${(props) => props.color};
  border: 1px solid ${(props) => props.border};
  padding: ${(props) => (props.mini ? '2px 6px' : '3px 8px')};
  border-radius: 6px;
  font-size: ${(props) => (props.mini ? '10px' : '11px')};
  font-weight: 700;
  white-space: nowrap;
`

const HistoricoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  background-color: rgba(245, 158, 11, 0.18);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.35);
  letter-spacing: 0.3px;
  cursor: help;
`

const ShareBarTrack = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
  height: 4px;
  border-radius: 4px;
  overflow: hidden;
`

const ShareBarFill = styled.div<{ pct: number }>`
  background: linear-gradient(90deg, #a6c10b, #4cc9f0);
  height: 100%;
  width: ${(props) => props.pct}%;
  border-radius: 4px;
  transition: width 0.4s ease;
`

// --- VISUALIZAÇÃO EM TABELA ENRIQUECIDA ---

const TableContainer = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.4)')};
  border: 1px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 12px;
  overflow-x: auto;
  box-shadow: ${(props) => (props.theme.name === 'light' ? '0 4px 15px rgba(0, 0, 0, 0.05)' : '0 4px 15px rgba(0, 0, 0, 0.25)')};

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #a6c10b;
    border-radius: 6px;
  }
`

const EnhancedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#cbd5e1')};

  th {
    background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 29, 83, 0.95)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#a6c10b')};
    padding: 10px 12px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.4px;
    border-bottom: 2px solid ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(166, 193, 11, 0.3)')};
    user-select: none;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)')};
    vertical-align: middle;
  }

  tr:hover td {
    background: ${(props) => (props.theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.04)')};
  }

  .table-total-row td {
    background: ${(props) => (props.theme.name === 'light' ? '#f1f5f9' : 'rgba(0, 0, 0, 0.4)')};
    color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#fff')};
    font-weight: 700;
    border-top: 2px solid ${(props) => (props.theme.name === 'light' ? '#a6c10b' : 'rgba(166, 193, 11, 0.4)')};
    border-bottom: none;
  }
`

const EmptyBox = styled.div`
  background: ${(props) => (props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.3)')};
  border: 1px dashed ${(props) => (props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
  border-radius: 12px;
  padding: 35px 20px;
  text-align: center;
  color: ${(props) => (props.theme.name === 'light' ? '#64748b' : '#8e9aaf')};

  .empty-icon {
    color: #475569;
    margin-bottom: 10px;
  }

  p {
    font-size: 12px;
    margin: 0 0 12px 0;
  }

  .reset-btn {
    background: #a6c10b;
    color: #000;
    border: none;
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: bold;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
  }
`
